// ABOUTME: Logout utility — clears local storage + Apollo cache, then ends the session.
// ABOUTME: In-app-auth ON → revoke the Kratos session via the logout flow, then go to /auth/login.

import config from "../../config"
import { clearPersistedCache } from "@/graphql/client/client"
import { STORAGE_KEYS } from "@shared/constants/storageKeys"
import { clearImportedWorkouts } from "@/model/health/importedWorkoutsStore"
import { isInAppAuthEnabled } from "@/model/auth/inAppAuthFlag"
import { ory } from "@/model/auth/ory"
import { notifyNativeLoggedOut } from "@/model/auth/nativeOidc"
import authSupport from "@/model/auth/authSupport"
import { createLogger } from "@/model/logger"
import { LOGOUT_PAGE, LOGIN_PAGE } from "@shared/constants/navigation"

const logger = createLogger("Logout")

const clearLocal = () => {
  localStorage.removeItem(STORAGE_KEYS.ROLE)
  // authSupport.clean() clears ALL auth credentials the app checks in
  // isSetSupportToken — crucially x-session-token2, the raw long-lived native
  // token from a Google/Apple login. Removing only a subset (as before) left
  // x-session-token2 behind, so the app still saw itself authenticated as the
  // old account after logout.
  authSupport.clean()
  // HealthKit workout IDs are per-device, so leaving them behind would show
  // the next athlete on this phone someone else's workouts as imported.
  clearImportedWorkouts()
  void clearPersistedCache()
}

const LOGOUT_REVOKE_TIMEOUT_MS = 4000

const withTimeout = async <T>(p: Promise<T>, ms: number): Promise<T> =>
  Promise.race([
    p,
    new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("logout revoke timeout")), ms)
    }),
  ])

// In-app logout: revoke the Kratos session server-side (we try, so we don't fake logout while the
// cookie stays valid — hardening H4), but bounded by a timeout so a stalled network can never hang
// the button: on timeout/failure we still clear local state and land on the login screen.
const logoutInApp = async () => {
  // Immediate feedback: if a button triggered this on some app screen, bounce to the /logout
  // spinner route first. That reload re-invokes logoutUser via the pre-app /logout handler, which
  // lands back here already on /logout and runs the revoke — so the user sees a spinner during the
  // up-to-4s revoke instead of a frozen screen with no indication.
  if (window.location.pathname !== "/logout") {
    window.location.href = LOGOUT_PAGE
    return
  }
  try {
    await withTimeout(
      (async () => {
        const { data } = await ory.createBrowserLogoutFlow()
        await ory.updateLogoutFlow({ token: data.logout_token })
      })(),
      LOGOUT_REVOKE_TIMEOUT_MS,
    )
  } catch (e) {
    logger.error("In-app logout: revoke failed or timed out; clearing locally anyway", { error: e })
  }
  clearLocal()
  // Drop the wrapper app's persisted native token before the full-page nav tears
  // down this context — otherwise it re-injects the old account on the next
  // WebView recreate. Awaited so the bridge message is delivered first.
  await notifyNativeLoggedOut()
  window.location.href = LOGIN_PAGE
}

export const logoutUser = () => {
  if (isInAppAuthEnabled()) {
    void logoutInApp()
    return
  }

  clearLocal()
  void notifyNativeLoggedOut()
  window.location.href = config.linkToLogout()
}
